import React, { ReactElement, useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Trans } from '@lingui/macro';
import { useHistory } from 'react-router';
import { message } from 'antd';

import { CandidatePositionRelation, CandidateShareInvite } from 'types';
import { StepWrapper } from '../Steps';
import SharedSelector, { SharedSelectorProps } from './steps/SharedSelector';
import SharedForm, { ShareFormValues } from './steps/ShareForm';
import ConfirmConditions from './steps/ConfirmConditions';
import { Alert } from 'components/AntD';
import './styles.less';

const steps = [
  {
    label: 1,
    desc: <Trans>Select References</Trans>,
    title: <Trans>Which references would you like to share?</Trans>,
    description: (
      <Trans>
        We&apos;ll only share the reference feedback you choose, without personal information of the
        references.
      </Trans>
    ),
  },
  {
    label: 2,
    desc: <Trans>Provide Contact Info</Trans>,
    title: <Trans>Who would you like to share with?</Trans>,
    description: (
      <Trans>
        We&apos;ll send them a secure invitation to view the references you&apos;ve selected.
      </Trans>
    ),
  },
  {
    label: 3,
    desc: <Trans>Confirm</Trans>,
    title: <Trans>Last step!</Trans>,
    description: <Trans>We take data and privacy very seriously.</Trans>,
  },
];

export interface CandidateShareStepperProps {
  references: SharedSelectorProps['references'];
  sharedData: Array<CandidateShareInvite>;
  appliedCompanies: CandidatePositionRelation[];
  onSubmit(options: {
    sharingInformation: ShareFormValues;
    selectedReferences: CandidateShareStepperProps['references'];
  }): void;
  mainTitle?: React.ReactNode;
}

export default function CandidateShareStepper({
  references,
  appliedCompanies,
  sharedData,
  onSubmit,
  mainTitle = <Trans>Share your references</Trans>,
}: CandidateShareStepperProps): JSX.Element {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(0);
  const [sharingInformation, setSharingInformation] = useState<ShareFormValues>({
    firstName: '',
    lastName: '',
    workEmail: '',
    company: '',
  });
  const [selectedReferences, setSelectedReferences] = useState(references);
  const [hasAcceptedConditions, setHasAcceptedConditions] = useState(false);

  const onConfirmConditionsSubmit = useCallback(async () => {
    try {
      await onSubmit({
        sharingInformation,
        selectedReferences,
      });
    } catch (e) {
      message.error('Something went wrong!');
    }
  }, [onSubmit, selectedReferences, sharingInformation]);

  const onNextStep = useCallback(() => setCurrentStep(currentStep + 1), [currentStep]);

  const onBack = useCallback(() => {
    if (currentStep === 0) {
      history.goBack();
      return;
    }
    setCurrentStep(currentStep - 1);
  }, [currentStep, history]);

  return (
    <div className="tw-container tw-max-w-7xl xl:tw-mx-auto">
      <StepWrapper
        className="tw-max-w-3xl"
        steps={steps}
        mainTitle={mainTitle as unknown as ReactElement}
        currentStep={currentStep}
        onBack={onBack}
      >
        <div className="content-wrapper">
          <SharedSelector
            references={references}
            onNextStep={onNextStep}
            values={selectedReferences}
            onSelectChanged={setSelectedReferences}
          />
        </div>
        <div className="content-wrapper">
          <div className="tw-flex tw-flex-col tw-gap-4">
            {(!isEmpty(appliedCompanies) || !isEmpty(sharedData)) && (
              <Alert
                showIcon={true}
                color="info"
                description={
                  <>
                    <span className="tw-text-base tw-font-medium tw-text-gray-800">
                      You have already shared your reference reports with:
                    </span>
                    <ul className="shared-references tw-mb-sm">
                      {appliedCompanies.map(
                        ({
                          position: {
                            company: { companyName, companyId },
                          },
                        }) => (
                          <li key={companyId}> {companyName}</li>
                        )
                      )}
                      {sharedData.map(
                        ({ candidateShareInviteId, inviteeFirstName, inviteeEmail }) => (
                          <li key={candidateShareInviteId}>
                            {inviteeFirstName} &mdash; {inviteeEmail}
                          </li>
                        )
                      )}
                    </ul>
                  </>
                }
              />
            )}
            <SharedForm
              appliedCompanies={appliedCompanies}
              onNextStep={onNextStep}
              information={sharingInformation}
              onSubmit={setSharingInformation}
              onBack={onBack}
            />
          </div>
        </div>
        <div className="content-wrapper">
          <ConfirmConditions
            isChecked={hasAcceptedConditions}
            onSubmit={() => onConfirmConditionsSubmit()}
            onChanged={e => setHasAcceptedConditions(e.target.checked)}
            inviteeEmail={sharingInformation.workEmail!}
            companyName={sharingInformation.company}
            firstName={sharingInformation.firstName!}
            onBack={onBack}
          />
        </div>
      </StepWrapper>
    </div>
  );
}
